import {
  Component, ElementRef, Input, OnChanges, OnDestroy, AfterViewInit,
  SimpleChanges, TemplateRef, forwardRef, inject,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ScrollingModule, VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import { RowHeightsVirtualScrollStrategy } from './row-heights-virtual-scroll-strategy';

// A tile (photo) or a full-width separator (e.g. a year label) interleaved
// into the grid — separators break the current row the same way they do in
// the non-virtualized layout this replaces.
export type VirtualGridItem<T> =
  | { type: 'tile'; data: T }
  | { type: 'separator'; label: string };

interface TileCell<T> { data: T; index: number; }
type VirtualRow<T> =
  | { kind: 'tiles'; cells: TileCell<T>[]; height: number }
  | { kind: 'separator'; label: string; key: string; height: number };

/**
 * Reusable dense photo grid with CDK virtual scrolling. Scrolls within the
 * app shell's actual scroll region (`<main cdkVirtualScrollingElement>` in
 * app.component.html) rather than owning its own scrollbar — picked up
 * automatically via DI, so this drops into the page layout unchanged.
 * Chunks the flat `items` list into rows of `columns` tiles — column count is
 * derived from the host's own width via ResizeObserver, mirroring the
 * `repeat(auto-fill, minmax(...))` grids this replaces. Separator rows (e.g.
 * year labels) get their own, much shorter row height via a custom
 * VirtualScrollStrategy (see row-heights-virtual-scroll-strategy.ts) instead
 * of CDK's default fixed-size strategy, which would force every separator to
 * reserve a full tile row's worth of space. Callers own tile markup entirely
 * via `cellTemplate`.
 */
@Component({
  selector: 'app-virtual-photo-grid',
  standalone: true,
  imports: [ScrollingModule, NgTemplateOutlet],
  providers: [{
    provide: VIRTUAL_SCROLL_STRATEGY,
    useFactory: (grid: VirtualPhotoGridComponent) => grid.scrollStrategy,
    deps: [forwardRef(() => VirtualPhotoGridComponent)],
  }],
  template: `
    <cdk-virtual-scroll-viewport class="vpg-viewport">
      <div *cdkVirtualFor="let row of rows; trackBy: trackRow" class="vpg-row" [style.height.px]="row.height">
        @if (row.kind === 'separator') {
          <div class="vpg-separator">{{ row.label }}</div>
        } @else {
          <div class="vpg-tiles"
            [style.gridTemplateColumns]="'repeat(' + columns + ', 1fr)'"
            [style.gap.px]="gap">
            @for (cell of row.cells; track cell.index) {
              <ng-container
                [ngTemplateOutlet]="cellTemplate"
                [ngTemplateOutletContext]="{ $implicit: cell.data, index: cell.index }">
              </ng-container>
            }
          </div>
        }
      </div>
    </cdk-virtual-scroll-viewport>
  `,
  styleUrls: ['./virtual-photo-grid.component.scss'],
})
export class VirtualPhotoGridComponent<T = any> implements OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) items: VirtualGridItem<T>[] = [];
  @Input({ required: true }) cellTemplate!: TemplateRef<any>;
  @Input() minTileWidth = 115;
  // Optional wider minimum once the grid itself has room — mirrors the
  // `@media (min-width: ...)` breakpoints the old CSS-only grids used, since
  // that switch has to happen in JS now that column count is computed here.
  @Input() minTileWidthWide?: number;
  @Input() wideBreakpoint = 600;
  @Input() gap = 4;
  @Input() aspectRatio = 1; // tile width / height, e.g. 1 for square
  // Height of a separator row — a compact label, not a photo tile.
  @Input() separatorHeight = 44;

  readonly scrollStrategy = new RowHeightsVirtualScrollStrategy();

  private hostEl = inject(ElementRef<HTMLElement>);
  private resizeObserver?: ResizeObserver;

  columns = 1;
  rows: VirtualRow<T>[] = [];

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.recompute());
    this.resizeObserver.observe(this.hostEl.nativeElement);
    this.recompute();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['minTileWidth'] || changes['gap'] || changes['aspectRatio'] || changes['separatorHeight']) {
      this.recompute();
    }
  }

  trackRow(_: number, row: VirtualRow<T>): string {
    return row.kind === 'separator' ? row.key : 'r' + row.cells[0]?.index;
  }

  private recompute(): void {
    const width = this.hostEl.nativeElement.clientWidth;
    if (!width) return;

    const minTileWidth = (this.minTileWidthWide && width >= this.wideBreakpoint)
      ? this.minTileWidthWide
      : this.minTileWidth;

    const columns = Math.max(1, Math.floor((width + this.gap) / (minTileWidth + this.gap)));
    const tileWidth = (width - this.gap * (columns - 1)) / columns;
    const tileHeight = tileWidth / this.aspectRatio;
    const rowHeight = Math.round(tileHeight + this.gap);

    this.columns = columns;
    this.rows = this.buildRows(this.items, columns, rowHeight);
    this.scrollStrategy.setRowHeights(this.rows.map(r => r.height));
  }

  private buildRows(items: VirtualGridItem<T>[], columns: number, rowHeight: number): VirtualRow<T>[] {
    const rows: VirtualRow<T>[] = [];
    let current: TileCell<T>[] = [];
    let sepCount = 0;

    const flush = () => {
      if (current.length) {
        rows.push({ kind: 'tiles', cells: current, height: rowHeight });
        current = [];
      }
    };

    items.forEach((item, index) => {
      if (item.type === 'separator') {
        flush();
        rows.push({ kind: 'separator', label: item.label, key: 'sep' + sepCount++ + '-' + item.label, height: this.separatorHeight });
        return;
      }
      current.push({ data: item.data, index });
      if (current.length === columns) flush();
    });
    flush();

    return rows;
  }
}
